import { either } from "fp-ts";
import { z } from "zod";

import { zAppConfig } from "./app";
import { zBackendAppConfig } from "./backend-app";
import { zGoogleConfig } from "./google";

function zConfig<
  UserRoleKey extends z.KeySchema,
  UserRoleValue extends z.ZodType<string, z.ZodTypeDef>,
>(userRoleEntry: [UserRoleKey, UserRoleValue]) {
  return z
    .object({
      NODE_ENV: z.enum(["dev", "prod"]),
      app: zAppConfig(userRoleEntry),
      backendApp: zBackendAppConfig,
      google: zGoogleConfig,
    })
    .transform(({ NODE_ENV, ...rest }) => ({
      nodeEnv: NODE_ENV,
      ...rest,
    }));
}

type Config<
  UserRoleKey extends z.KeySchema,
  UserRoleValue extends z.ZodType<string, z.ZodTypeDef>,
> = z.infer<ReturnType<typeof zConfig<UserRoleKey, UserRoleValue>>>;

function createConfig<
  UserRoleKey extends z.KeySchema,
  UserRoleValue extends z.ZodType<string, z.ZodTypeDef>,
>(
  variables: Record<string, unknown> & { NODE_ENV?: string | undefined },
  userRoleEntry: [UserRoleKey, UserRoleValue],
): either.Either<Error, Config<UserRoleKey, UserRoleValue>> {
  const configSchema = zConfig(userRoleEntry);
  return either.tryCatch(
    () =>
      configSchema.parse({
        ...Object.fromEntries(
          Object.keys(configSchema.innerType().keyof().Values).map((key) => [key, variables]),
        ),
        NODE_ENV: variables.NODE_ENV,
      }),
    (error) => {
      if (error instanceof z.ZodError) {
        return new Error(`Failed to create application configuration. Cause: ${error.toString()}`);
      }
      throw error;
    },
  );
}

export { createConfig, zConfig };
export type { Config };
